import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { promptService, type Prompt } from '../../services/promptService';
import { queryKeys } from '../../lib/react-query';
import type { PaginatedResponse } from '../../services/apiService';

export interface SearchFilters {
  query: string;
  page?: number;
  per_page?: number;
}

export interface SearchResultSet {
  prompts: Prompt[];
  pagination: PaginatedResponse<Prompt>['pagination'] | null;
}

const sanitizeFilters = (filters: SearchFilters): SearchFilters => {
  const trimmedQuery = filters.query?.trim() ?? '';
  return {
    query: trimmedQuery,
    page: filters.page,
    per_page: filters.per_page,
  };
};

export const useSearchResults = (
  filters: SearchFilters,
  options: { enabled?: boolean; staleTime?: number } = {}
) => {
  const sanitized = sanitizeFilters(filters);
  const enabled = options.enabled ?? sanitized.query.length >= 2;

  return useQuery<SearchResultSet>({
    queryKey: queryKeys.prompts.search(sanitized.query, {
      page: sanitized.page,
      per_page: sanitized.per_page,
    }),
    enabled,
    staleTime: options.staleTime,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: (previousData) => previousData,
    queryFn: async () => {
      const response = await promptService.searchPrompts(sanitized.query, {
        page: sanitized.page,
        per_page: sanitized.per_page,
      });

      return {
        prompts: response.data,
        pagination: response.pagination,
      } satisfies SearchResultSet;
    },
  });
};

const serializeFilters = (filters: SearchFilters): URLSearchParams => {
  const params = new URLSearchParams();
  const sanitized = sanitizeFilters(filters);

  if (sanitized.query) {
    params.set('query', sanitized.query);
  }

  if (sanitized.page) {
    params.set('page', sanitized.page.toString());
  }

  if (sanitized.per_page) {
    params.set('per_page', sanitized.per_page.toString());
  }

  return params;
};

const parseNumberParam = (value: string | null): number | undefined => {
  if (!value) {
    return undefined;
  }

  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

export const useSearchUrl = () => {
  const buildUrl = (filters: SearchFilters) => {
    const params = serializeFilters(filters);
    const search = params.toString();
    return search ? `/search?${search}` : '/search';
  };

  const buildParams = (filters: SearchFilters) => {
    return serializeFilters(filters);
  };

  const parseUrl = (searchParams: URLSearchParams): SearchFilters => {
    const query = searchParams.get('query') ?? searchParams.get('q') ?? '';
    const page = parseNumberParam(searchParams.get('page'));
    const perPage = parseNumberParam(searchParams.get('per_page'));

    return {
      query,
      ...(page ? { page } : {}),
      ...(perPage ? { per_page: perPage } : {}),
    };
  };

  return {
    buildUrl,
    buildParams,
    parseUrl,
  };
};

export const useSearchSummary = (results?: SearchResultSet) => {
  return useMemo(() => {
    const promptsCount = results?.prompts.length ?? 0;
    const total = results?.pagination?.total ?? promptsCount;

    if (total === 0) {
      return 'No prompts found';
    }

    if (total === promptsCount) {
      return `Showing ${promptsCount} prompt${promptsCount === 1 ? '' : 's'}`;
    }

    return `Showing ${promptsCount} of ${total} prompts`;
  }, [results]);
};
